import React, { useEffect, useState } from "react";
import BackButton from "../components/BackButton";

const s = {
  wrapper: { minHeight: "100svh", background: "#f3f4f6", padding: "16px 16px 40px", boxSizing: "border-box", fontFamily: "system-ui, -apple-system, sans-serif" },
  inner: { maxWidth: "680px", margin: "0 auto" },
  card: { background: "white", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", padding: "24px 20px", marginBottom: "12px" },
  h1: { fontSize: "22px", fontWeight: 800, color: "#1e293b", margin: "0 0 4px" },
  tagline: { fontSize: "13px", color: "#64748b", margin: "0 0 0" },
  sectionTitle: { fontSize: "13px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" },
  qRow: { borderBottom: "1px solid #f1f5f9", cursor: "pointer" },
  qRowLast: { cursor: "pointer" },
  qHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", gap: "12px" },
  qText: { fontSize: "14px", fontWeight: 600, color: "#1e293b", flex: 1, lineHeight: 1.45 },
  chevron: { fontSize: "16px", color: "#94a3b8", flexShrink: 0, display: "inline-block", transition: "transform 0.18s ease", userSelect: "none" },
  body: { fontSize: "14px", color: "#475569", lineHeight: 1.7, margin: "0 0 14px" },
  ol: { fontSize: "14px", color: "#475569", lineHeight: 1.7, paddingLeft: "20px", margin: "6px 0 10px" },
  footer: { fontSize: "13px", color: "#94a3b8", textAlign: "center", marginTop: "8px", lineHeight: 1.6 },
};

const SECTIONS = [
  {
    emoji: "🛒",
    title: "Pour les clients",
    questions: [
      {
        q: "Le scan ne reconnaît pas mon produit, que faire ?",
        body: "CrushNow utilise plusieurs bases de données produits (Open Food Facts, EAN-Search…). Si ton article n'est dans aucune, le scan échoue. Tu peux dans ce cas créer une fiche communautaire : remplis manuellement le nom, la marque et la catégorie, et le produit sera ajouté pour toi et les autres utilisateurs. Ça nous aide tous à enrichir la base.",
      },
      {
        q: "Pourquoi certains produits n'ont pas de photo ?",
        body: "Les photos viennent des bases de données produits open source. Elles ne sont pas toujours renseignées, surtout pour les produits non alimentaires ou de niche. C'est un point qu'on améliore en continu — et tu peux contribuer en complétant les fiches communautaires.",
      },
      {
        q: "Je n'ai pas reçu de notification, c'est normal ?",
        intro: "Trois explications possibles :",
        items: [
          "Le vendeur n'a pas encore mis à jour son produit — pas de modif = pas de notif. C'est conçu comme ça pour éviter le spam.",
          "Tu n'as pas activé l'alerte 🔔 sur le produit dans tes Crushs.",
          "Les notifications sont désactivées sur ton téléphone pour CrushNow. Vérifie dans les paramètres Android.",
        ],
        ordered: true,
        after: "Si rien de tout ça ne s'applique, écris-nous, on regarde de près.",
      },
      {
        q: "Pourquoi CrushNow demande ma localisation ?",
        body: "La géolocalisation est optionnelle et sert à deux choses : (1) associer ton scan au magasin le plus proche (rayon 500m) pour que tu reçoives uniquement les notifications des magasins où tu as scanné — pas de ceux situés à l'autre bout de la France ; (2) enrichir ton historique avec le nom de la ville. Tu peux refuser : l'app fonctionne quand même, tu recevras simplement les notifications de tous les vendeurs qui ont ce produit, sans filtre géographique. Aucune donnée de localisation n'est partagée avec les vendeurs ou des tiers.",
      },
      {
        q: "Qu'est-ce qu'un lien de partage de crush ?",
        body: "Quand tu partages un produit depuis ta Crush List, CrushNow génère un lien public unique (ex: crush-now.fr/c/...). Ce lien permet à n'importe qui — même sans compte — de voir la fiche du produit. Ton identité n'est jamais visible sur cette page : seul le produit est affiché. Tu peux partager ce lien par WhatsApp, SMS ou tout autre moyen.",
      },
      {
        q: "À quoi servent les magasins favoris ?",
        body: "Les magasins favoris te permettent de choisir les commerces dont tu veux recevoir les notifications. Si tu as crushed un produit dans plusieurs magasins, tu ne recevras des alertes que de ceux que tu as ajoutés en favoris. Tu peux ajouter ou retirer un magasin favori depuis la carte dans l'app.",
      },
      {
        q: "Comment supprimer un crush de ma liste ?",
        body: "Depuis l'onglet Crushs, utilise le bouton ✕ sur le produit pour le retirer. Tu peux aussi vider tout l'historique d'un coup depuis la même page. Attention : la suppression est définitive.",
      },
      {
        q: "Je ne vois pas le prix d'un produit, pourquoi ?",
        body: "Les prix sont renseignés par les vendeurs qui référencent le produit sur CrushNow. Si aucun vendeur n'a encore référencé ce produit dans ta zone, le prix n'apparaît pas. C'est aussi pour ça que tu actives une alerte 🔔 — tu seras prévenu dès qu'un vendeur ajoute l'info.",
      },
      {
        q: "Mes XP et badges sont-ils sauvegardés si je change de téléphone ?",
        body: "Oui ! Depuis la version 1.1, ta progression (XP, niveau, badges) est sauvegardée sur nos serveurs et liée à ton compte. Tu peux changer de téléphone, réinstaller l'app ou te connecter depuis un autre appareil — ta progression est toujours là.\n\nTu gagnes des XP en scannant des produits (+1 XP), en ajoutant des fiches communautaires (+5 XP), en partageant tes crushes (+2 XP), en enregistrant un achat (+3 XP) et en complétant ton profil (+10 XP). 12 badges sont à débloquer selon ton activité.",
      },
      {
        q: "Comment fonctionne le système de niveaux et badges ?",
        body: "CrushNow a un système de progression inspiré de Google Local Guides. Voici comment ça marche :\n\n🎯 Gagner des XP :\n- Scanner un produit → +1 XP (max 10/jour)\n- Ajouter un produit communautaire → +5 XP (max 5/jour)\n- Partager un crush → +2 XP (max 5/jour)\n- Enregistrer un achat → +3 XP (max 10/jour)\n- Compléter ton profil (genre/âge) → +10 XP (une fois)\n\n🏆 Les 7 niveaux :\n- Niveau 1 — Curieux (0 XP)\n- Niveau 2 — Explorateur (25 XP)\n- Niveau 3 — Chasseur (75 XP)\n- Niveau 4 — Crusheur (150 XP)\n- Niveau 5 — Expert (300 XP)\n- Niveau 6 — Ambassadeur (500 XP)\n- Niveau 7 — Légende (1000 XP)\n\n🎖 Les 12 badges :\n🔍 Premier scan · 🛍️ Premier achat · 📦 Contributeur · 🌟 Super contributeur · 📤 Partageur · 🔥 Viral · 💎 Fidèle (30j de streak) · 🗺️ Explorateur (3 villes) · 🏆 Crusheur expert (niveau 5) · 👑 Légende (niveau 7) · 🤝 Social (10 partages) · ⚡ Achat rapide (< 24h après scan)\n\nTa progression est visible dans ton profil. Tu reçois une notification quand tu montes de niveau !",
      },
    ],
  },
  {
    emoji: "🏪",
    title: "Pour les vendeurs",
    questions: [
      {
        q: "Comment référencer mon commerce sur CrushNow ?",
        body: "Créez un compte vendeur depuis la page de connexion (rôle « Vendeur »), puis ajoutez vos produits depuis votre tableau de bord en scannant leur code-barres. Chaque produit devient visible pour les clients qui l'ont déjà ajouté à leurs Crushs.",
      },
      {
        q: "Combien ça coûte ?",
        body: "C'est gratuit pendant la phase de lancement. Un abonnement vendeur sera proposé plus tard pour soutenir l'hébergement et le développement du service, à un tarif raisonnable et transparent. Les vendeurs présents pendant la phase de lancement bénéficieront de conditions préférentielles.",
      },
      {
        q: "Est-ce que mes clients reçoivent vraiment mes notifications ?",
        body: "Oui — dès qu'un client a activé une alerte 🔔 sur l'un de vos produits, il reçoit automatiquement une notification quand vous modifiez le prix, le stock ou le statut. Aucune action manuelle de votre part : la notification est envoyée par CrushNow. À noter : si le client a désactivé les notifications de l'app sur son téléphone, le message ne s'affichera pas. Le taux de réception dépend aussi du modèle d'appareil et de la version Android.",
      },
      {
        q: "Puis-je gérer plusieurs boutiques avec un seul compte ?",
        body: "Pas encore. Pour le moment, un compte = un commerce. La gestion multi-boutiques (utile pour les franchises ou les enseignes) est prévue dans une version ultérieure.",
      },
      {
        q: "Comment voir qui a scanné mes produits ?",
        body: "Cliquez sur l'un de vos produits depuis le tableau de bord pour ouvrir sa fiche complète. Vous y verrez le nombre de scans et le nombre de clients alertés sur ce produit, ainsi que l'historique de vos modifications. Pour des raisons de confidentialité, l'identité des clients n'est jamais visible — seuls les chiffres agrégés le sont.",
      },
    ],
  },
  {
    emoji: "⚙️",
    title: "Compte et confidentialité",
    questions: [
      {
        q: "Quelle différence entre un compte email et un compte pseudonyme ?",
        intro: "CrushNow propose deux modes de création de compte :",
        items: [
          "Compte email : classique, avec récupération par email en cas de mot de passe oublié.",
          "Compte pseudonyme : aucune information personnelle requise. À la création, vous recevez 12 mots de récupération à conserver précieusement. Ces mots remplacent le mot de passe et l'email pour récupérer votre compte.",
        ],
        ordered: false,
        after: "Le compte pseudonyme est conçu pour celles et ceux qui veulent utiliser CrushNow sans laisser d'identifiant personnel. Aucun email, aucun numéro, aucun lien avec votre identité réelle.",
      },
      {
        q: "J'ai perdu mes 12 mots de récupération, comment faire ?",
        body: "Malheureusement, les 12 mots sont la seule clé de récupération d'un compte pseudonyme. Si vous les avez perdus, votre compte ne peut pas être récupéré — c'est le prix de l'anonymat total. Nous vous recommandons fortement de les noter sur papier ou dans un gestionnaire de mots de passe dès la création du compte. Pour les comptes email, utilisez la fonction « Mot de passe oublié » sur la page de connexion.",
      },
      {
        q: "Mes données sont-elles vendues ou partagées avec des tiers ?",
        body: "Non. Vos scans, votre historique, votre localisation et vos données de compte ne sont jamais vendus ni partagés avec des tiers commerciaux. Les seules données techniques transmises sont celles nécessaires au fonctionnement de l'app (hébergement sécurisé, envoi de notifications). Notre politique de confidentialité détaille tout cela précisément.",
      },
      {
        q: "Y a-t-il de la publicité dans CrushNow ?",
        body: "Non, et il n'y en aura pas. C'est un choix fondateur : CrushNow ne diffuse aucune publicité. Le modèle économique repose sur l'abonnement vendeur, pas sur l'attention des utilisateurs.",
      },
      {
        q: "Comment supprimer définitivement mon compte ?",
        body: "Depuis votre profil, rendez-vous dans Mes données → Supprimer mon compte. Toutes vos données (scans, crushs, profil, progression) sont effacées immédiatement de nos serveurs. Cette action est irréversible.",
      },
      {
        q: "Comment exporter mes données ?",
        body: "Depuis votre profil, allez dans Mes données → Exporter mes données. Vous recevrez un fichier au format JSON contenant l'intégralité de vos informations (compte, scans, crushs, paramètres). C'est votre droit à la portabilité, garanti par le RGPD.",
      },
      {
        q: "Où sont stockées mes données ?",
        body: "Vos données sont actuellement hébergées sur MongoDB Atlas, un service cloud sécurisé. La migration vers un hébergement strictement européen est planifiée pour renforcer notre conformité RGPD. Toutes les communications entre l'app et le serveur sont chiffrées (HTTPS), et les mots de passe sont protégés par bcrypt.",
      },
    ],
  },
];

const FAQPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openKey, setOpenKey] = useState(null);

  const toggle = (key) => setOpenKey(openKey === key ? null : key);

  return (
    <div style={s.wrapper}>
      <div style={s.inner}>
        <BackButton />

        <div style={s.card}>
          <h1 style={s.h1}>💡 Foire aux questions</h1>
          <p style={{ ...s.tagline, marginBottom: "0" }}>
            Tu as une question sur CrushNow ? La réponse est probablement ici.
            Si tu ne trouves pas, écris-nous via « Donner mon avis ».
          </p>
        </div>

        {SECTIONS.map((section, si) => (
          <div key={si} style={s.card}>
            <p style={s.sectionTitle}>{section.emoji} {section.title}</p>

            {section.questions.map((q, qi) => {
              const key = `${si}-${qi}`;
              const isOpen = openKey === key;
              const isLast = qi === section.questions.length - 1;

              return (
                <div
                  key={qi}
                  style={isLast ? s.qRowLast : s.qRow}
                  onClick={() => toggle(key)}
                >
                  <div style={s.qHeader}>
                    <span style={s.qText}>{q.q}</span>
                    <span style={{ ...s.chevron, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>
                      {">"}
                    </span>
                  </div>

                  {isOpen && (
                    <div style={{ paddingBottom: "14px" }}>
                      {q.intro && <p style={s.body}>{q.intro}</p>}
                      {q.body && <p style={s.body}>{q.body}</p>}
                      {q.items && (
                        q.ordered
                          ? (
                            <ol style={s.ol}>
                              {q.items.map((item, ii) => <li key={ii}>{item}</li>)}
                            </ol>
                          )
                          : (
                            <ul style={s.ol}>
                              {q.items.map((item, ii) => <li key={ii}>{item}</li>)}
                            </ul>
                          )
                      )}
                      {q.after && <p style={s.body}>{q.after}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <p style={s.footer}>
          Une question manquante ? Écris-nous via « Donner mon avis » dans l'app — on lit tout, et la FAQ s'enrichit de tes retours.
        </p>
      </div>
    </div>
  );
};

export default FAQPage;
